import React, { useState, useMemo, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  FolderGit2,
  Loader2,
  RefreshCw,
  Play,
  Plus,
} from "lucide-react";

import Layout, { PageHeader, PageContainer } from "../components/ui/Layout";
import { triggerScan, syncReposFromGitHub } from "../api/client";
import { useRepos, prefetchRepoFindings } from "../api/hooks";
import ScanProgressModal from "../components/ScanProgressModal";
import { cn } from "../lib/utils";

import { Card, CardContent, CardHeader, CardTitle } from "../components/shadcn/card";
import { Badge } from "../components/shadcn/badge";
import { Button } from "../components/shadcn/button";
import { Input } from "../components/shadcn/input";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/shadcn/table";
import { Skeleton } from "../components/shadcn/skeleton";
import { Separator } from "../components/shadcn/separator";

// ─── Helpers ──────────────────────────────────────────────────────

function formatLastScan(dateStr?: string): string {
  if (!dateStr) return "Never";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// ─── Types ────────────────────────────────────────────────────────

interface RepoRow {
  id: string;
  name: string;
  fullName: string;
  url: string;
  defaultBranch: string;
  lastScanAt?: string;
  openFindings: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

// ─── Component ────────────────────────────────────────────────────

export default function ReposList() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [scanningRepoId, setScanningRepoId] = useState<string | null>(null);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanJobId, setScanJobId] = useState<string | null>(null);
  const [scanRepoName, setScanRepoName] = useState("");

  // Fetch repos
  const reposQuery = useRepos();
  const queryError = reposQuery.error;

  // Prefetch findings on repo hover for instant navigation
  const handleRepoPrefetch = useCallback(
    (repoId: string) => prefetchRepoFindings(qc, repoId),
    [qc]
  );

  // Transform repos to local row format
  const repos: RepoRow[] = useMemo(() => {
    return (reposQuery.data || []).map((repo) => ({
      id: repo.id,
      name: repo.url.split("/").pop() || "",
      fullName: repo.url.replace("https://github.com/", ""),
      url: repo.url,
      defaultBranch: repo.default_branch || "main",
      lastScanAt: repo.last_scan_at,
      openFindings:
        (repo.open_critical || 0) +
        (repo.open_high || 0) +
        (repo.open_medium || 0) +
        (repo.open_low || 0),
      critical: repo.open_critical || 0,
      high: repo.open_high || 0,
      medium: repo.open_medium || 0,
      low: repo.open_low || 0,
    }));
  }, [reposQuery.data]);

  // Filter repos by search
  const filteredRepos = useMemo(() => {
    if (!search.trim()) return repos;
    const query = search.toLowerCase();
    return repos.filter(
      (repo) =>
        repo.fullName.toLowerCase().includes(query) ||
        repo.name.toLowerCase().includes(query)
    );
  }, [repos, search]);

  // Sync from GitHub
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleSyncFromGitHub = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await syncReposFromGitHub();
      if (result.success) {
        setSyncMessage({
          type: "success",
          text: `Synced ${result.synced} repositories from GitHub`,
        });
        reposQuery.refetch();
      } else {
        setSyncMessage({
          type: "error",
          text: result.errors?.join(", ") || "Sync failed",
        });
      }
    } catch (err: any) {
      console.error("Sync failed:", err);
      setSyncMessage({
        type: "error",
        text: err.message || "Failed to sync repositories",
      });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  // Scan mutation
  const scanMutation = useMutation({
    mutationFn: async (repoId: string) => {
      setScanningRepoId(repoId);
      const repo = repos.find((r) => r.id === repoId);
      setScanRepoName(repo?.fullName || "Repository");
      return triggerScan(repoId, { scanType: "baseline" });
    },
    onSuccess: (data) => {
      setScanJobId(data.job_id);
      setScanModalOpen(true);
      // Keep scanningRepoId set so the button stays disabled while scan runs
    },
    onError: (error) => {
      console.error("Scan failed:", error);
      setScanningRepoId(null);
      setScanJobId(null);
      setScanModalOpen(true);
    },
  });

  const handleScan = (repoId: string) => {
    scanMutation.mutate(repoId);
  };

  const handleScanComplete = () => {
    const completedRepoId = scanningRepoId;
    qc.invalidateQueries({ queryKey: ["repos"] });
    setScanModalOpen(false);
    setScanningRepoId(null);
    if (completedRepoId) {
      navigate(`/dashboard/repos/${completedRepoId}`);
    }
  };

  const isLoading = reposQuery.isLoading;

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <Layout>
      {/* Scan Progress Modal */}
      <ScanProgressModal
        isOpen={scanModalOpen}
        onClose={() => {
          setScanModalOpen(false);
          setScanningRepoId(null);
        }}
        onComplete={handleScanComplete}
        onFinished={() => {
          // Scan completed or failed on the backend; re-enable scan buttons
          setScanningRepoId(null);
        }}
        repoName={scanRepoName}
        jobId={scanJobId}
      />

      <PageContainer>
        {/* Page Header */}
        <PageHeader
          title="Repositories"
          description={`${repos.length} repositories connected`}
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncFromGitHub}
                disabled={syncing}
              >
                <RefreshCw
                  className={cn("mr-2 h-4 w-4", syncing && "animate-spin")}
                />
                {syncing ? "Syncing..." : "Sync from GitHub"}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => reposQuery.refetch()}
                disabled={reposQuery.isFetching}
              >
                <RefreshCw
                  className={cn(
                    "h-4 w-4",
                    reposQuery.isFetching && "animate-spin"
                  )}
                />
              </Button>
              <Button asChild size="sm">
                <Link to="/onboarding">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Repositories
                </Link>
              </Button>
            </div>
          }
        />

        {/* Sync message */}
        {syncMessage && (
          <div
            className={cn(
              "mb-4 rounded-lg border px-4 py-3 text-sm font-medium",
              syncMessage.type === "success"
                ? "border-green-500/20 bg-green-500/10 text-green-600 dark:text-green-400"
                : "border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400"
            )}
          >
            {syncMessage.text}
          </div>
        )}

        {/* Search */}
        <div className="mb-6 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search repositories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <span className="text-sm text-muted-foreground tabular-nums">
            {filteredRepos.length !== repos.length
              ? `${filteredRepos.length} of ${repos.length}`
              : `${repos.length} repositories`}
          </span>
        </div>

        <Separator className="mb-6" />

        {/* Content */}
        {isLoading ? (
          /* Loading skeleton rows */
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Repository</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-center">Open</TableHead>
                  <TableHead className="text-center">Critical</TableHead>
                  <TableHead className="text-center">High</TableHead>
                  <TableHead>Last Scan</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-5 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-16" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="h-5 w-10 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="h-5 w-10 mx-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="h-5 w-10 mx-auto" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-20" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-8 w-16 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : queryError ? (
          /* Error state */
          <Card className="text-center">
            <CardHeader>
              <FolderGit2 className="h-10 w-10 text-red-500 mx-auto" />
              <CardTitle className="text-lg">
                Failed to load repositories
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {(queryError as Error)?.message ||
                  "Unable to fetch repositories. Please try again."}
              </p>
              <Button onClick={() => reposQuery.refetch()}>Retry</Button>
            </CardContent>
          </Card>
        ) : filteredRepos.length === 0 ? (
          /* Empty state */
          <Card className="text-center">
            <CardHeader>
              <FolderGit2 className="h-12 w-12 text-muted-foreground mx-auto" />
              <CardTitle className="text-lg">
                {repos.length === 0
                  ? "No repositories found"
                  : "No matching repositories"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {repos.length === 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    No repositories found. Sync from GitHub to get started.
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button variant="outline" onClick={handleSyncFromGitHub} disabled={syncing}>
                      <RefreshCw className={cn("mr-2 h-4 w-4", syncing && "animate-spin")} />
                      Sync from GitHub
                    </Button>
                    <Button asChild>
                      <Link to="/onboarding">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Repositories
                      </Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Try adjusting your search query
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Repos table */
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Repository</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-center">Open</TableHead>
                  <TableHead className="text-center">Critical</TableHead>
                  <TableHead className="text-center">High</TableHead>
                  <TableHead>Last Scan</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRepos.map((repo) => (
                  <TableRow
                    key={repo.id}
                    className="cursor-pointer"
                    onMouseEnter={() => handleRepoPrefetch(repo.id)}
                    onClick={() => navigate(`/dashboard/repos/${repo.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FolderGit2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="font-medium">{repo.fullName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {repo.defaultBranch}
                    </TableCell>
                    <TableCell className="text-center">
                      {repo.openFindings > 0 ? (
                        <Badge variant="outline">{repo.openFindings}</Badge>
                      ) : (
                        <Badge variant="secondary">0</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {repo.critical > 0 ? (
                        <Badge variant="critical">{repo.critical}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {repo.high > 0 ? (
                        <Badge variant="high">{repo.high}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatLastScan(repo.lastScanAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={scanningRepoId === repo.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleScan(repo.id);
                        }}
                      >
                        {scanningRepoId === repo.id ? (
                          <>
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            Scanning
                          </>
                        ) : (
                          <>
                            <Play className="mr-1 h-3.5 w-3.5" />
                            Scan
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </PageContainer>
    </Layout>
  );
}
